import { formatToDollar, formatToPercent } from "@inrev/common";
import { SuretyAccountPreview } from "@portal/api/account/agent/types";
import type { DynamicDataTableColumn } from "@portal/components/ui/DynamicDataTable/types";
import { RowActionButtons } from "@portal/components/ui/row-action-buttons/RowActionButtons";
import { getStatusTagByStatus, statusDisplayLabelMap } from "@portal/components/ui/StatusTag";
import { DateTime } from "luxon";
import { formatDateForCsv, formatEmpty } from "../../../utils";
import { AdminAssigneeControls } from "../shared/AdminAssigneeControls";
import { AgencyCellView } from "./AgencyCellView";

export type AdminAccountPreview = SuretyAccountPreview & {
	underwriterName: string;
	agencyName: string;
};

export const adminAccountsTableColumns: readonly DynamicDataTableColumn<AdminAccountPreview>[] = [
	{
		id: "underwriter",
		accessorKey: "underwriterName",
		headerFilter: { type: "checkbox" },
		cellStyle: { noTruncate: true },
		csv: { rowObjectKey: "underwriterName", fileColumnHeader: "Underwriter" },
		size: 60,
		enableSorting: true,
		cell: ({ row }) =>
			row.original.underwriterUserId ? (
				<RowActionButtons alwaysShow={true}>
					<AdminAssigneeControls
						assignedEntity={{ entityType: "account", entityId: row.original.id }}
						currentAssignee={row.original.underwriterUserId}
					/>
				</RowActionButtons>
			) : null,
	},
	{
		id: "agencyId",
		accessorFn: (row) => row.agencyName ?? row.agencyId,
		header: "Agency",
		size: 120,
		minSize: 88,
		enableResizing: true,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
		csv: { rowObjectKey: "agencyName", fileColumnHeader: "Agency" },
		cell: ({ row }) => <AgencyCellView agencyId={row.original.agencyId} />,
	},
	{
		id: "status",
		accessorFn: (row) => statusDisplayLabelMap[row.status] ?? row.status,
		header: "Status",
		size: 205,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
		cell: ({ row }) => getStatusTagByStatus(row.original.status),
	},
	{
		id: "displayName",
		accessorFn: ({ displayName }) => formatEmpty(displayName),
		header: "Name",
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
		cell: ({ row }) => formatEmpty(row.original.displayName),
	},
	{
		id: "rate",
		accessorFn: ({ contract }) => contract?.bondingLine?.rate ?? 0,
		header: "Rate",
		size: 80,
		enableSorting: true,
		cell: ({ row }) => formatEmpty(formatToPercent(row.original.contract?.bondingLine?.rate)),
	},
	{
		id: "singleLimit",
		accessorFn: ({ contract }) => contract?.bondingLine?.singleLimit ?? 0,
		header: "Single Limit",
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
		cell: ({ row }) => formatEmpty(formatToDollar(row.original.contract?.bondingLine?.singleLimit)),
	},
	{
		id: "aggregateLimit",
		accessorFn: ({ contract }) => contract?.bondingLine?.aggregateLimit ?? 0,
		header: "Aggregate Limit",
		size: 180,
		minSize: 150,
		enableResizing: true,
		enableSorting: true,
		cell: ({ row }) =>
			formatEmpty(formatToDollar(row.original.contract?.bondingLine?.aggregateLimit)),
	},
	{
		id: "totalExposure",
		accessorFn: ({ contract }) => contract?.exposure.total ?? 0,
		header: "Total Exposure",
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
		cell: ({ row }) => formatEmpty(formatToDollar(row.original.contract?.exposure.total)),
	},
	{
		id: "submittedAt",
		accessorFn: ({ submittedAt }) =>
			submittedAt !== undefined ? DateTime.fromISO(submittedAt).toJSDate() : undefined,
		header: "Submitted",
		size: 150,
		enableSorting: true,
		sortingFn: "datetime",
		csv: { formatValue: formatDateForCsv },
		cell: ({ row }) =>
			formatEmpty(
				row.original.submittedAt
					? DateTime.fromISO(row.original.submittedAt).toLocaleString(DateTime.DATE_MED)
					: row.original.submittedAt,
			),
	},
	{
		id: "entityTags",
		header: "Tags",
		accessorFn: ({ entityTags }) => entityTags?.map((tag) => tag.displayName) ?? [],
		enableResizing: true,
		enableSorting: true,
		enableHiding: true,
		headerFilter: { type: "checkbox-array" },
		cell: ({ row }) => row.original.entityTags?.map((tag) => tag.displayName).join(", ") || "--",
	},
];
